import { useQuery } from '@tanstack/react-query';
import { useRef, useCallback } from 'react';
import { api } from '@/lib/api-client';
import type { ListLinksResponse, ListLinksRequest } from '@readlater/shared';

interface UseLinksOptions {
  filter?: 'unread' | 'favorites' | 'archived';
  enabled?: boolean;
}

// Exponential backoff config for polling processing links
const POLL_INITIAL_MS = 2000; // Start at 2 seconds
const POLL_MAX_MS = 30000; // Cap at 30 seconds
const POLL_MULTIPLIER = 1.5; // Increase by 50% each time

export function useLinks(options: UseLinksOptions = {}) {
  const { filter, enabled = true } = options;
  const pollIntervalRef = useRef(POLL_INITIAL_MS);

  // Build query params based on filter
  const getQueryParams = (): ListLinksRequest => {
    switch (filter) {
      case 'unread':
        return { is_read: false, is_archived: false };
      case 'favorites':
        return { is_favorite: true };
      case 'archived':
        return { is_archived: true };
      default:
        return {};
    }
  };

  const queryParams = getQueryParams();
  const queryString = new URLSearchParams(
    Object.entries(queryParams)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [k, String(v)])
  ).toString();

  // Calculate refetch interval based on whether links are processing
  const getRefetchInterval = useCallback((query: { state: { data?: ListLinksResponse } }) => {
    const data = query.state.data;
    const hasProcessingLinks = data?.links?.some(
      (l) => l.status === 'pending' || l.status === 'processing'
    );

    if (!hasProcessingLinks) {
      // Reset backoff when no processing links
      pollIntervalRef.current = POLL_INITIAL_MS;
      return false;
    }

    // Get current interval and schedule next with backoff
    const currentInterval = pollIntervalRef.current;
    pollIntervalRef.current = Math.min(currentInterval * POLL_MULTIPLIER, POLL_MAX_MS);

    return currentInterval;
  }, []);

  return useQuery({
    queryKey: ['links', filter || 'all'],
    queryFn: () => api.get<ListLinksResponse>(`/links${queryString ? `?${queryString}` : ''}`),
    enabled,
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    refetchInterval: getRefetchInterval,
  });
}
